import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Play, Users } from "lucide-react";
import { HERO_SLIDES, SUMMIT } from "@/constants/summit";
import { MaskedLine } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const INTERVAL = 6000;

const HeroCarousel = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const next = useCallback(() => setActive((a) => (a + 1) % HERO_SLIDES.length), []);
  const prev = () => setActive((a) => (a - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    if (paused) return;
    const i = setInterval(next, INTERVAL);
    return () => clearInterval(i);
  }, [next, paused]);

  const slide = HERO_SLIDES[active];

  return (
    <section
      id="overview"
      ref={ref}
      data-testid="hero-carousel-container"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={active}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        >
          <motion.img
            src={slide.image}
            alt=""
            className="h-full w-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: 1.14 }}
            transition={{ duration: INTERVAL / 1000 + 2, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-28 sm:px-8 sm:pb-32"
      >
        <div key={`kicker-${active}`} className="mb-6 flex items-center gap-4">
          <span className="h-px w-10 bg-brand-500" />
          <MaskedLine delay={0.1} className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-brand-300">
            {slide.kicker}
          </MaskedLine>
        </div>

        <h1
          key={`headline-${active}`}
          data-testid="hero-headline"
          className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tight leading-[0.98] text-white"
        >
          {slide.lines.map((line, i) => (
            <MaskedLine key={i} delay={0.2 + i * 0.14}>
              {i === 0 && active === 0 ? <span className="text-brand-500">{line}</span> : line}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          key={`sub-${active}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
        >
          {slide.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/confirm-attendance"
            data-testid="hero-rsvp-primary-button"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 font-display text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-600 hover:shadow-[0_0_40px_rgba(255,87,34,0.55)]"
          >
            Confirm Attendance
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/speakers"
            data-testid="hero-meet-speakers-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display text-sm font-semibold text-white/85 backdrop-blur-sm transition-all duration-300 hover:border-brand-400 hover:text-brand-300"
          >
            <Users className="h-4 w-4" /> Meet the Speakers
          </Link>
          {slide.cta && (
            <button
              key={`cta-${active}`}
              data-testid="hero-video-invitation-button"
              onClick={() => scrollToSection(slide.cta.target)}
              className="group inline-flex items-center gap-2 rounded-full border border-brand-500/60 bg-brand-500/15 px-7 py-3.5 font-display text-sm font-semibold text-brand-300 backdrop-blur-sm transition-all duration-300 hover:bg-brand-500 hover:text-white"
            >
              <Play className="h-4 w-4 fill-current" /> {slide.cta.label}
            </button>
          )}
          <span className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/45">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse" />
            {SUMMIT.dateLabel} · {SUMMIT.city}
          </span>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 right-5 sm:right-8 z-20 flex items-center gap-3">
        <button
          data-testid="hero-slide-prev-button"
          onClick={prev}
          aria-label="Previous banner"
          className="grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white/80 backdrop-blur-sm transition-all duration-300 hover:border-brand-400 hover:text-brand-300"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          data-testid="hero-slide-next-button"
          onClick={next}
          aria-label="Next banner"
          className="grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white/80 backdrop-blur-sm transition-all duration-300 hover:border-brand-400 hover:text-brand-300"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="absolute bottom-8 left-5 sm:left-8 z-20 flex items-center gap-2.5">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => setActive(i)}
            aria-label={`Go to banner ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === active ? "w-10 bg-brand-500" : "w-4 bg-white/25 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroCarousel;
